// src/composables/useChartDownload.ts
import type { Ref } from 'vue'
import { buildCaptionLines, buildExportFilename } from '@/utils/chartExport'

interface DownloadContext {
  titleText?: string
  projectName?: string
  methodName?: string
  filename?: string
}

// Minimal shape we rely on from a vue-chartjs wrapper ref (Bar/Line): it exposes
// a `.chart` Chart.js instance with a `canvas` and `toBase64Image()`.
interface ChartRefLike {
  chart?: {
    canvas: HTMLCanvasElement
    toBase64Image: () => string
  }
}

/**
 * PNG export shared by BarChart/LineChart. When project/method context is given,
 * a white caption band ("Projekt: …", "Methode: …") is composited above the raw
 * chart canvas; otherwise the chart is exported as-is. Filename is derived from
 * title/project/method via chartExport.
 */
export function useChartDownload(chartRef: Ref<ChartRefLike | null>, ctx: () => DownloadContext) {
  function triggerDownload(url: string) {
    const { titleText, projectName, methodName, filename } = ctx()
    const link = document.createElement('a')
    link.href = url
    link.download = buildExportFilename(titleText, projectName, methodName, filename)
    link.click()
  }

  function downloadImage() {
    const chart = chartRef.value?.chart
    if (!chart) return

    const { projectName, methodName } = ctx()
    const captionLines = buildCaptionLines(projectName, methodName)

    if (!captionLines.length) {
      triggerDownload(chart.toBase64Image())
      return
    }

    const sourceCanvas = chart.canvas
    const scale = Math.max(window.devicePixelRatio || 1, 1)
    const padding = 12 * scale
    const lineHeight = 20 * scale
    const bandHeight = padding * 2 + captionLines.length * lineHeight

    const exportCanvas = document.createElement('canvas')
    exportCanvas.width = sourceCanvas.width
    exportCanvas.height = sourceCanvas.height + bandHeight

    const c = exportCanvas.getContext('2d')
    if (!c) return
    c.fillStyle = '#ffffff'
    c.fillRect(0, 0, exportCanvas.width, exportCanvas.height)

    c.fillStyle = '#24292f'
    c.font = `${14 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif`
    c.textBaseline = 'top'
    captionLines.forEach((line, i) => {
      c.fillText(line, padding, padding + i * lineHeight)
    })

    c.drawImage(sourceCanvas, 0, bandHeight)

    triggerDownload(exportCanvas.toDataURL('image/png'))
  }

  return { downloadImage }
}
